import React from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { KvRow } from "kontrolEdge/components/Sdwan/shared/sdwanFormat";
import type { Fwaas } from "@shared/components/fwaas/FwaasData";
import type { HaMember, VmSpecs } from "kontrolEdge/components/Shared/VmSpecsCard";
import type { FwaasOverview } from "../overview/overview.types";
import { EMPTY, NO_READING, daysSince, firmwareSummary, shortDate, timeAgo } from "../overview/overviewFormat";
import type { FirewallRouting } from "../routing/routing.types";
import { RoutingSummary } from "../routing/RoutingSummary";
import type { Reading } from "../readings/useFwaasReading";
import type { AttentionItem } from "../attention/needsAttention";
import type { FirewallEvents } from "../events/events.types";
import { errorsSummary } from "../events/eventsFormat";
import type { Tunnels } from "../tunnels/tunnels";
import { drawnIds, haPair, type MapNode } from "./serviceMap";
import { eventsOfNode } from "./nodeDetails";
import { EventList, InspectorAttention, InspectorHead, InspectorSection } from "./MapInspector";

const HA_MODE: Record<string, string> = {
    "active-passive": "active / passive",
    "active-active": "active / active",
    standalone: "standalone",
};

function HighAvailability({ overview, members }: { overview: FwaasOverview | null; members: HaMember[] }) {
    const ha = overview?.ha;
    if (overview === null) return <p className="text-sm text-semantic-text-secondary">Reading…</p>;
    if (!ha) return <p className="text-sm text-semantic-text-secondary">{NO_READING}</p>;
    if (ha.mode === "standalone") return <p className="text-sm">One unit, no failover.</p>;
    const pair = haPair(ha, members);
    return (
        <>
            {pair.length > 0 && (
                <ul className="rounded-lg border border-semantic-border-primary bg-semantic-bg-primary">
                    {pair.map((member, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm border-b border-semantic-border-primary last:border-b-0">
                            <span>
                                Member {String.fromCharCode(65 + i)}
                                <span className="block text-xs text-semantic-text-secondary">{member.role}</span>
                            </span>
                            <GenericTag label={member.up ? "Up" : "Down"} variant={member.up ? "success" : "error"} size="small" />
                        </li>
                    ))}
                </ul>
            )}
            <KvRow label="Configuration">{ha.in_sync == null ? EMPTY : ha.in_sync ? "In sync" : "Out of sync"}</KvRow>
            <KvRow label="Last failover">{ha.last_failover_at ? shortDate(ha.last_failover_at) : EMPTY}</KvRow>
        </>
    );
}

function Uptime({ overview }: { overview: FwaasOverview }) {
    const since = overview.uptime?.since;
    const days = since ? daysSince(since) : null;
    if (!since || days == null) return <KvRow label="Up for">{NO_READING}</KvRow>;
    return (
        <KvRow label="Up for">
            {days} {days === 1 ? "day" : "days"}
            <span className="block text-xs text-semantic-text-secondary">Since {shortDate(since)}</span>
        </KvRow>
    );
}

function Firmware({ overview }: { overview: FwaasOverview }) {
    if (!overview.firmware) return <KvRow label="Running">{NO_READING}</KvRow>;
    const { value, update } = firmwareSummary(overview.firmware);
    return (
        <>
            <KvRow label="Running">{value}</KvRow>
            {update && (
                <KvRow label="Recommended">
                    {update}
                    <span className="block text-xs text-semantic-text-secondary">Newest mature release</span>
                </KvRow>
            )}
        </>
    );
}

function Routing({ routing }: { routing: Reading<FirewallRouting> }) {
    if (routing === undefined) return <p className="text-sm text-semantic-text-secondary">Reading…</p>;
    if (routing === null) return <p className="text-sm text-semantic-text-secondary">{NO_READING}</p>;
    return <RoutingSummary routing={routing} />;
}

/**
 * Whether the tunnels could be read at all: `null` is "we could not ask", never "no
 * tunnels" (CTX-760) — without this row the map would simply draw no tunnel nodes.
 */
function tunnelsLabel(tunnels: Tunnels | null): string {
    if (tunnels === null) return "Unavailable right now";
    const siteToSite = tunnels.items.filter((t) => t.kind === "site-to-site").length;
    const dialUp = tunnels.items.length - siteToSite;
    if (tunnels.items.length === 0) return "None configured";
    return [siteToSite ? `${siteToSite} site-to-site` : null, dialUp ? `${dialUp} remote access` : null].filter(Boolean).join(" · ");
}

/** "29 errors in the last 24 h · log back to 3 h ago": how far the firewall's short log reaches. */
function eventsSubtitle(events: FirewallEvents): string {
    return [errorsSummary(events), events.since ? `log back to ${timeAgo(events.since)}` : null].filter(Boolean).join(" · ");
}

/**
 * The firewall's own inspector, open when the page loads (CTX-1209): its HA pair, how
 * long it has been up, its firmware, its routing, and the events that belong to no other
 * node (CTX-1265). Members go by role, never serial.
 */
export default function FirewallInspector({
    service,
    vm,
    haMembers,
    overview,
    routing,
    tunnels,
    events,
    nodes,
    attention,
    titleRef,
    onClose,
}: {
    service: Fwaas;
    vm: VmSpecs | null;
    haMembers: HaMember[];
    overview: FwaasOverview | null;
    routing: Reading<FirewallRouting>;
    tunnels: Tunnels | null;
    events: Reading<FirewallEvents>;
    nodes: MapNode[];
    attention: AttentionItem[];
    titleRef?: React.Ref<HTMLHeadingElement>;
    onClose: () => void;
}) {
    const mode = overview?.ha ? HA_MODE[overview.ha.mode] ?? overview.ha.mode : null;
    const subtitle = [vm?.platform, vm?.vcpu != null ? `${vm.vcpu} vCPU` : null, mode].filter(Boolean).join(" · ");
    return (
        <>
            <InspectorHead
                kicker="Virtual firewall"
                title={service.subscriptionId ?? service.name}
                subtitle={subtitle || undefined}
                titleRef={titleRef}
                onClose={onClose}
            />
            <InspectorAttention items={attention} />
            <InspectorSection label="High availability">
                <HighAvailability overview={overview} members={haMembers} />
                {overview && <Uptime overview={overview} />}
            </InspectorSection>
            {overview && (
                <InspectorSection label="Firmware">
                    <Firmware overview={overview} />
                </InspectorSection>
            )}
            <InspectorSection label="Routing">
                <Routing routing={routing} />
                <KvRow label="IPsec tunnels">{tunnelsLabel(tunnels)}</KvRow>
            </InspectorSection>
            <InspectorSection label="Events">
                {events ? <p className="text-xs text-semantic-text-secondary">{eventsSubtitle(events)}</p> : null}
                <EventList events={eventsOfNode(events, tunnels, "firewall", drawnIds(nodes))} empty="Nothing else logged recently." />
            </InspectorSection>
        </>
    );
}
